import React, { useEffect, useState } from "react";
import classNames from "classnames";
import dayjs from "dayjs";

import Button from "components/botanic/Button";

import { AgeGateFormProps } from "./AgeGate";

const CANADIAN_REGION_REQUIREMENTS: Record<
  string,
  { age: number; restrictedRegion?: boolean }
> = {
  Alberta: {
    age: 18,
  },
  "British Columbia": {
    age: 19,
  },
  Manitoba: {
    age: 19,
  },
  "New Brunswick": {
    age: 19,
  },
  "Newfoundland & Labrador": {
    age: 19,
  },
  "Northwest Territories": {
    age: 19,
  },
  "Nova Scotia": {
    age: 19,
  },
  Nunavut: {
    age: 19,
  },
  Ontario: {
    age: 19,
  },
  "Prince Edward Island": {
    age: 19,
  },
  Québec: {
    age: 19,
    restrictedRegion: true,
  },
  Saskatchewan: {
    age: 19,
  },
  Yukon: {
    age: 19,
  },
};

const TODAY = dayjs();
const DEFAULT_REGION = "Select a region";

const CanadianAgeGateForm: React.FC<AgeGateFormProps> = ({
  confirm,
  setRegionSelected,
}) => {
  const [region, setRegion] = useState<
    typeof DEFAULT_REGION | keyof typeof CANADIAN_REGION_REQUIREMENTS
  >(DEFAULT_REGION);
  const [year, setYear] = useState<string | number>("Year");
  const [month, setMonth] = useState<string | number>("Month");
  const [day, setDay] = useState<string | number>("Day");
  const [validationStarted, startValidation] = useState<boolean>(false);

  const hasSelectedRegion = region !== DEFAULT_REGION;
  const currentYear = new Date().getFullYear();
  const ageRequirement =
    region === DEFAULT_REGION
      ? Infinity
      : CANADIAN_REGION_REQUIREMENTS[region].age;
  const userBirthDate = dayjs(`${year}-${month}-${day}`);

  const isRestrictedRegion =
    hasSelectedRegion && CANADIAN_REGION_REQUIREMENTS[region].restrictedRegion;
  const yearFilledOut = year !== "Year";
  const monthFilledOut = month !== "Month";
  const dayFilledOut = day !== "Day";
  const ageFilledOut = yearFilledOut && monthFilledOut && dayFilledOut;
  const ageInvalid =
    ageFilledOut &&
    year > 0 &&
    month > 0 &&
    day > 0 &&
    TODAY.diff(userBirthDate, "year") < ageRequirement;
  const formAndAgeValid =
    hasSelectedRegion &&
    year > 0 &&
    month > 0 &&
    day > 0 &&
    TODAY.diff(userBirthDate, "year") >= ageRequirement &&
    !isRestrictedRegion;

  const validateOrConfirm = () => {
    if (formAndAgeValid) {
      confirm();
    } else {
      startValidation(true);
    }
  };

  const onYearSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const year = parseInt(e.target.value);
    setYear(year);
  };
  const onMonthSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const month = parseInt(e.target.value);
    setMonth(month);
  };
  const onDaySelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const day = parseInt(e.target.value);
    setDay(day);
  };

  useEffect(() => {
    setRegionSelected?.(hasSelectedRegion);
  }, [hasSelectedRegion]);

  return (
    <>
      <div className="pb-sm">
        <span className="text-xs mr-xs">Which part of Canada?</span>
        <select
          className="bg-light-grey rounded font-bold items-center text-sm p-md px-lg z-10"
          data-testid="age-gate-modal-region-select"
          aria-label="Select a Region"
          defaultValue={region}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
            setRegion(
              (e.target as HTMLSelectElement)
                .value as keyof typeof CANADIAN_REGION_REQUIREMENTS
            )
          }
        >
          {!hasSelectedRegion && (
            <option value={DEFAULT_REGION}>Select a region</option>
          )}
          {Object.entries(CANADIAN_REGION_REQUIREMENTS).map(([regionKey]) => {
            return (
              <option key={regionKey} value={regionKey}>
                {regionKey}
              </option>
            );
          })}
        </select>
      </div>
      {isRestrictedRegion && (
        <p>Sorry, Leafly isn’t available in your location yet.</p>
      )}
      {hasSelectedRegion && !isRestrictedRegion && (
        <div>
          <span className="text-xs mr-xs">When were you born?</span>
          <div className="flex flex-row pb-xs">
            <div className="flex-initial lg:flex-1 pb-sm">
              <select
                data-testid="age-gate-modal-year-select"
                className={classNames(
                  "bg-light-grey rounded font-bold items-center text-sm p-md px-lg pr-xxl",
                  {
                    "border-error": validationStarted && !yearFilledOut,
                  }
                )}
                defaultValue={year}
                onChange={onYearSelect}
                aria-label="Pick your birth year"
              >
                <option disabled>Year</option>
                {[...Array(currentYear - 1900)]
                  .map((e, i) => {
                    return (
                      <option key={i} value={i + 1901}>{`${i + 1901}`}</option>
                    );
                  })
                  .reverse()}
              </select>
            </div>
            <div className="flex-1 px-md pb-sm">
              <select
                data-testid="age-gate-modal-month-select"
                className={classNames(
                  "bg-light-grey rounded font-bold items-center text-sm p-md px-lg pr-xl",
                  {
                    "border-error": validationStarted && !monthFilledOut,
                  }
                )}
                defaultValue={month}
                onChange={onMonthSelect}
                aria-label="Pick your birth month"
              >
                <option disabled>Month</option>
                <option value="1">January</option>
                <option value="2">February</option>
                <option value="3">March</option>
                <option value="4">April</option>
                <option value="5">May</option>
                <option value="6">June</option>
                <option value="7">July</option>
                <option value="8">August</option>
                <option value="9">September</option>
                <option value="10">October</option>
                <option value="11">November</option>
                <option value="12">December</option>
              </select>
            </div>
            <div className="flex-initial lg:flex-1 pb-sm">
              <select
                data-testid="age-gate-modal-day-select"
                className={classNames(
                  "bg-light-grey rounded font-bold items-center text-sm p-md px-lg pr-xxl",
                  {
                    "border-error": validationStarted && !dayFilledOut,
                  }
                )}
                defaultValue={day}
                onChange={onDaySelect}
                aria-label="Pick your birth day"
              >
                <option disabled>Day</option>
                {[...Array(31)].map((e, i) => {
                  return <option key={i} value={i + 1}>{`${i + 1}`}</option>;
                })}
              </select>
            </div>
          </div>
          {ageInvalid && (
            <p className="text-error" data-testid="age-gate-modal-invalid">
              Darn! You're not old enough to use Leafly.
            </p>
          )}
          {hasSelectedRegion && (
            <div className="pt-md pb-sm">
              <Button
                variant="primary"
                onClick={validateOrConfirm}
                data-testid="age-gate-yes-button"
              >
                enter
              </Button>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default CanadianAgeGateForm;
